import { flexRender, Header as TableHeader } from "@tanstack/react-table";
import { cn } from "@theprelude/core";
import { ArrowDown, ArrowUp, SortIcon, Truncate } from "@theprelude/ds";
import { forwardRef, useLayoutEffect, useState } from "react";
import { DefaultTooltip } from "../default-tooltip";

export const Header: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="flex min-w-0 flex-row items-center gap-2 font-medium text-base text-primary-foreground">
      <Truncate text={children}>{children}</Truncate>
    </div>
  );
};

export const SortIndicator: React.FC<{
  order: "asc" | "desc" | false;
}> = ({ order }) => {
  if (order === "asc") {
    return (
      <ArrowUp className="h-4 w-4 text-secondary-foreground hover:fill-primary-foreground" />
    );
  } else if (order === "desc") {
    return (
      <ArrowDown className="h-4 w-4 text-secondary-foreground hover:fill-primary-foreground" />
    );
  } else {
    return (
      <SortIcon className="h-4 w-4 text-secondary-foreground hover:fill-primary-foreground" />
    );
  }
};

export const VirtualSortedHeader = forwardRef<
  HTMLTableHeaderCellElement,
  {
    header: TableHeader<any, any>;
    onResize: () => void;
  }
>(({ header, onResize }, ref) => {
  const [pendingEvent, setPendingEvent] = useState<React.MouseEvent | null>(
    null,
  );

  const headerEl = header.column.columnDef.meta?.label ? (
    <Header>{header.column.columnDef.meta.label}</Header>
  ) : (
    header.column.columnDef.header
  );

  const isResizing = header.column.getIsResizing();
  const resizeHandler = header.getResizeHandler();

  useLayoutEffect(() => {
    if (pendingEvent) {
      resizeHandler(pendingEvent);
      setPendingEvent(null);
    }
  }, [pendingEvent, resizeHandler]);

  const handleMouseDown = (event: React.MouseEvent) => {
    onResize();
    setPendingEvent(event);
  };

  return (
    <th ref={ref} key={header.id} className={cn("group relative flex p-2")}>
      <div
        className={cn(
          "flex flex-row items-center gap-2 hover:cursor-pointer hover:select-none",
        )}
        onClick={header.column.getToggleSortingHandler()}
      >
        {flexRender(headerEl, header.getContext())}

        {header.column.getCanSort() &&
          !header.column.columnDef.meta?.hideSorting && (
            <SortIndicator order={header.column.getIsSorted()} />
          )}
      </div>

      {header.column.getCanResize() && (
        <div
          className={cn(
            "absolute top-0 right-0 h-full w-2 cursor-col-resize opacity-0 hover:opacity-100 group-hover:opacity-100",
            isResizing && "opacity-100",
          )}
          onMouseDown={handleMouseDown}
        >
          <DefaultTooltip asChild content="Resize column" disabled={isResizing}>
            <div
              className={cn(
                "h-full w-1 bg-stroke-line",
                isResizing && "bg-primary-foreground",
              )}
            />
          </DefaultTooltip>
        </div>
      )}
    </th>
  );
});

VirtualSortedHeader.displayName = "VirtualSortedHeader";
